



























const ObjectsToCsv = require("objects-to-csv");

import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import {
  Ingredient,
  IngredientsDiff,
  ingredientsTotalAmount,
  ingredientsTotalPrice,
} from "@megarax/client/src/resources/plm/Ingredient";
import ResourceItemHyperlink from "@/components/resource/ResourceItemHyperlink";
import {
  sheetOptionsSymbol,
  SheetHighlights,
} from "@/components/sheet/SheetEntries";
import { DecimalStringInput } from "@/components/form/InputFields";
import {
  createComponent,
  ref,
  inject,
  computed,
  watch,
} from "@vue/composition-api";
import fileDownload from "js-file-download";
import ListUnitTable from "@/components/ListUnitTable.vue";
import { komponentResource } from "@megarax/client/src/resources/plm/Komponent";
import ResourceItemReference from "@/components/resource/ResourceItemReference";
import Decimal from "decimal.js";
import IngredientForm from "./IngredientForm.vue";
import { SummaryMethodParams } from "element-ui/types/table";
import { TableColumnConfig, TableConfig } from "@/components/BaseTable.vue";
import { ingredientValidator } from "./core";
import { canAddToUniqueList, InputValidationError } from "@/shared/validators";
import BaseEditableTable from "@/components/BaseEditableTable.vue";

interface Props {
  value: Ingredient[];
  targetAmount: Decimal;
}

const formatPercentage = (num: Decimal) => num.mul(100).toFixed(2) + "%";

export default createComponent<Props>({
  components: {
    ResourceItemHyperlink,
    DecimalStringInput,
    ResourceItemReference,

    ListUnitTable,
    BaseEditableTable,
  },
  filters: {
    // TODO get rid of filters
    percentage(num: number) {
      return `${(num * 100).toFixed(2)}%`;
    },
    decimal(num: number) {
      return num.toFixed(2);
    },
  },
  props: {
    value: {
      required: true,
    },
    targetAmount: {
      required: true,
      type: Decimal,
    },
  },
  setup(props, ctx) {
    const columns: TableConfig = {
      komponent: {
        label: "Komponent",
        getSummary: () => "Razem",
      },
      ingredientGroup: {
        label: "Frakcja",
        sortable: true,
        formatter: (item) => (item ? item.str : "-"),
        sortMethod: ({ ingredientGroup: a }, { ingredientGroup: b }) => {
          if (a === b) return 0;
          if (a === null) return 1;
          if (b === null) return -1;
          if (a.str > b.str) return 1;
          if (a.str < b.str) return -1;
          return 0;
        },
      },
      massKg: {
        label: "Ilość [kg]",
        getSummary: () => props.targetAmount.toFixed(3),
      },
      price: {
        label: "Koszt składnika [PLN]",
        getSummary: () =>
          `≈${totalPricePerTargetAmount.value.toFixed(
            2,
          )} (${totalPricePerKg.value.toFixed(2)} PLN/kg)`,
      },
      roles: {
        label: "Funkcje",
        formatter: (roles) => roles.map((role: any) => role.str).join(", "),
      },
    };

    const totalIngredientAmount = computed<Decimal>(() =>
      ingredientsTotalAmount(props.value),
    );
    const totalPricePerKg = computed(() => ingredientsTotalPrice(props.value));
    const totalPricePerTargetAmount = computed(() =>
      ingredientsTotalPrice(props.value).mul(props.targetAmount),
    );

    function ingredientAmountShare(amount: Decimal): Decimal {
      if (totalIngredientAmount.value.equals(0)) return new Decimal(0);
      return amount.div(totalIngredientAmount.value as Decimal);
    }

    function targetIngredientAmount(amount: Decimal): Decimal {
      return ingredientAmountShare(amount).mul(props.targetAmount);
    }

    function ingredientCostShare(ingredient: Ingredient): Decimal {
      if (totalPricePerKg.value.equals(0)) return new Decimal(0);
      return ingredient
        .komponent!.price.mul(ingredientAmountShare(ingredient.massKg))
        .div(totalPricePerKg.value as Decimal);
    }

    function targetIngredientPrice(ingredient: Ingredient): Decimal {
      return ingredient.komponent!.price.mul(
        targetIngredientAmount(ingredient.massKg),
      );
    }

    async function onCsvButtonClick() {
      const data = props.value.map((skl: Ingredient) => ({
        komponent: skl.komponent!.str,
        "ilosc[g]": skl.massKg,
        "koszt[PLN]": skl.cost!.toFixed(2),
      }));

      const csv = new ObjectsToCsv(data);

      fileDownload(await csv.toString(), "składniki.csv");
    }

    const unitIsEqual = (a: Ingredient, b: Ingredient) =>
      a.komponent.id === b.komponent.id;

    function addItem(item: any) {
      item = ingredientValidator(item);
      if (!canAddToUniqueList(unitIsEqual)(props.value, item)) {
        throw new InputValidationError({
          komponent: "Ten komponent jest już na liście składników.",
        });
      }
      props.value.push(item);
    }
    function removeItem(original: Ingredient) {
      const index = props.value.findIndex((x) => x.uuid === original.uuid);
      props.value.splice(index, 1);
    }
    function updateItem(original: Ingredient, updated: any) {
      updated = ingredientValidator(updated);
      const index = props.value.findIndex((x) => x === original);
      const listWithoutOriginalItem = props.value.filter((x) => x !== original);
      if (!canAddToUniqueList(unitIsEqual)(listWithoutOriginalItem, updated)) {
        throw new InputValidationError({
          komponent: "Ten komponent jest już na liście składników.",
        });
      }
      props.value.splice(index, 1, updated);
    }

    return {
      IngredientForm,
      createNewItem: () => new Ingredient(),
      komponentResource,
      onCsvButtonClick,
      ingredientAmountShare,
      ingredientCostShare,
      targetIngredientAmount,
      targetIngredientPrice,
      totalPricePerKg,
      totalPricePerTargetAmount,
      columns,
      addItem,
      removeItem,
      updateItem,
    };
  },
});
